/* eslint-disable vue/no-template-key */
<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <div class="d-flex mb-8">
        <h2 class="font-weight-bold mb-6">{{ projectId ? $t('carbonCredit.register.editTitle') : $t('carbonCredit.register.title') }}</h2>
        <v-spacer></v-spacer>
        <div>
          <v-btn outlined class="mr-3" @click="$router.push({ name: 'listProjects' })"
            style="border-color: #00bd73; color: #00bd73">{{
              $t("carbonCredit.cancel") }}</v-btn>
          <v-btn color="primary" @click="submit">{{
            projectId ? $t("carbonCredit.save") : $t("carbonCredit.submit")
          }}</v-btn>
        </div>
      </div>
      <v-form ref="form">
        <v-card flat class="pa-6">
          <!-- Tenant is chosen globally in the app bar (super admin); this form creates into activeTenant. -->
          <v-row gap="1" class="mb-6">
            <v-col cols="6">
              <label class="font-weight-light mb-1">{{
                $t("carbonCredit.register.projectTitle")
              }}</label>
              <v-text-field v-model="form.project_title" outlined dense
                :placeholder="$t('carbonCredit.register.projectTitle')" />
            </v-col>

            <v-col cols="3">
              <label class="font-weight-light mb-1">{{
                $t("carbonCredit.register.projectType")
              }}</label>
              <v-select
                v-model="form.project_type"
                :items="projectTypes"
                outlined
                dense
                :placeholder="$t('carbonCredit.select')"
                item-text="text"
                item-value="value"
                @change="onProjectTypeChange"
              />
            </v-col>

            <v-col cols="3">
              <label class="font-weight-light mb-1">{{
                $t("carbonCredit.register.plotTreeMapping")
              }}</label>
              <v-select
                v-model="form.plot_tree_mapping_enabled"
                :items="plotTreeMappingOptions"
                outlined
                dense
                item-text="text"
                item-value="value"
                :disabled="!plotTreeMappingApplicable"
              />
            </v-col>

            <v-col cols="3">
              <label class="font-weight-light mb-1">{{
                $t("carbonCredit.register.pinVerification")
              }}</label>
              <v-select
                v-model="form.pin_verification_enabled"
                :items="pinVerificationOptions"
                outlined
                dense
                item-text="text"
                item-value="value"
              />
              <p class="text-caption gray-color mt-1">{{ $t('carbonCredit.register.pinVerificationHint') }}</p>
            </v-col>

            <v-col cols="12">
              <label class="font-weight-light mb-1">{{
                $t("carbonCredit.register.description")
              }}</label>
              <v-textarea v-model="form.description" outlined dense
                :placeholder="$t('carbonCredit.descriptionPlaceholder')" rows="3" auto-grow />
            </v-col>

            <v-col cols="12">
              <label class="font-weight-light mb-1">{{
                $t("carbonCredit.register.projectAgreement")
              }}</label>
              <div class="file-upload-section mt-4">
                <v-row>
                  <v-col cols="12" sm="8">
                    <div class="upload-document">
                      <div :class="['dropZone', { 'dropZone-over': dragging }]" @dragenter="dragging = true"
                        @dragleave="dragging = false" @drop="handleDrop($event)">
                        <div class="dropZone-info d-flex justify-center align-center" @dragover.prevent @click="triggerAgreementFileInput">
                          <v-icon size="50" color="#0057BD">mdi-tray-arrow-up</v-icon>
                          <div class="dropZone-upload-limit-info">
                            <h2 class="ml-4 blue-color underline">
                              {{ $t('carbonCredit.register.selectFile') }}
                            </h2>
                            <p class="gray-color">{{ $t('carbonCredit.register.dragDropFiles') }}</p>
                          </div>
                        </div>
                        <input type="file" multiple
                          accept="application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.csv,application/vnd.ms-excel,.xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,.mp3,.wav,.aac,.ogg,.flac,.mp4,.mov,.webm,.mkv"
                          @change="onFileChange($event)"
                          ref="agreementFileInput" />
                      </div>
                    </div>
                    <template v-if="uploadedDocuments.length">
                      <div class="pdf-docs mt-10 d-flex flex-column flex-sm-row"
                        v-for="(doc, index) in uploadedDocuments" :key="`doc-${index}-${doc.name || doc.file_name || doc.id}`">
                        <div class="docs-box mr-4 mb-4 mb-sm-0">
                          <div class="docs-preview">
                            <div class="img-item">
                              <div class="pdf-box">
                                <v-icon class="delete-icon" @click="removeFile(index)">mdi-trash-can-outline</v-icon>
                                <h1 class="file-extension">
                                  {{ getFileExtension(doc) }}
                                </h1>
                              </div>
                              <span class="file-name">
                                {{ getFileName(doc) }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </v-col>
                </v-row>
              </div>
            </v-col>

            <v-col cols="12">
              <label class="font-weight-light mb-1">{{
                $t("carbonCredit.register.headerImage")
              }}</label>
              <br>
              <label class="font-weight-light mb-1">{{
                $t("carbonCredit.register.headerImageInfo")
              }}</label>
              <template>
                <v-row justify="start">
                  <v-col cols="auto" class="text-center">
                    <v-card outlined class="upload-card d-flex align-center justify-center pa-4"
                      style="position: relative;">
                      <div class="text-center">
                        <template>
                          <div class="d-flex flex-column align-center">
                            <img src="/img/camera.svg" alt="Camera Logo" />
                            <input type="file" ref="fileInput" accept="image/*" style="display: none"
                              @change="handleFileUpload" />
                            <v-btn class="mx-2 mt-2" height="44" width="auto" depressed color="primary"
                              @click="triggerFileInput">
                              {{ $t('blends.manageProducts.addImage') }}
                            </v-btn>
                          </div>
                        </template>
                      </div>
                    </v-card>

                    <div class="mt-4">
                      <template v-if="headerImageUrl">
                        <div style="position: relative; width: 720px; height: 170px;">
                          <img :src="headerImageUrl" alt="Uploaded Image"
                            style="width: 100%; height: 100%; object-fit: contain; border-radius: 8px;" />
                          <v-icon class="delete-icon" @click="deleteImage()">mdi-trash-can-outline</v-icon>
                        </div>
                      </template>
                      <template v-else>
                        <div class="d-flex flex-column align-start">
                          <label class="font-weight-light mb-1">
                            {{ $t("carbonCredit.register.headerImagePlaceholder") }}
                          </label>
                          <img src="../../../assets/img/header-image-default.png" alt="Placeholder Image"
                            style="width: 720px; height: 170px; object-fit: contain;" />
                        </div>
                      </template>
                    </div>
                  </v-col>
                </v-row>
              </template>
            </v-col>
          </v-row>

          <!-- Responsible Dimitra Office -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                {{ $t("carbonCredit.register.responsibleDimitraOffice") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mt-4" align="center" justify="start">
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="form.dimitraOfficeId"
                      :items="dimitraOffices"
                      item-text="officeName"
                      item-value="id"
                      dense
                      outlined
                      :placeholder="$t('carbonCredit.register.makeASelection')"
                      :loading="loadingDimitraOffices"
                      :disabled="loadingDimitraOffices"
                      clearable
                    />
                  </v-col>
                  <div class="d-flex align-center mb-7 ml-2">
                    <v-btn
                      color="primary"
                      @click="form.dimitraOfficeId ? onEditDimitraOffice() : (showAddDimitraOffice = true)"
                    >
                      {{
                        form.dimitraOfficeId
                          ? ($t("carbonCredit.register.editDimitraOffice") || "Edit Dimitra Office")
                          : $t("carbonCredit.register.addNewDimitraOffice")
                      }}
                    </v-btn>
                  </div>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>


          <!-- Organization Details -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                {{ $t("carbonCredit.register.organizationDetails") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mt-4" align="center" justify="start">
                  <v-col cols="12" sm="6" md="4">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.selectCountry")
                    }}</label>
                    <v-select 
                      v-model="form.country" 
                      :items="countries" 
                      item-text="name" 
                      item-value="name" 
                      dense
                      outlined 
                      :placeholder="$t('carbonCredit.register.selectCountry')"
                      clearable
                      @change="onCountryChange"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <label class="font-weight-medium mb-1">
                      {{ $t("carbonCredit.register.selectOrganization") }}
                    </label>
                    <v-menu
                      v-model="organizationMenu"
                      :close-on-content-click="false"
                      offset-y
                      >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-bind="attrs"
                          v-on="on"
                          :value="selectedOrganizationText"
                          outlined
                          dense
                          readonly
                          :loading="loadingOrganizations"
                          :disabled="loadingOrganizations"
                          :placeholder="$t('carbonCredit.register.selectOrganization')"
                        ></v-text-field>
                      </template>

                      <v-card>
                        <v-list dense class="organization-list">
                          <v-list-item>
                            <v-text-field
                              v-model="organizationSearch"
                              outlined
                              dense
                              hide-details
                              :placeholder="$t('carbonCredit.search')"
                              prepend-inner-icon="mdi-magnify"
                              :disabled="loadingOrganizations"
                            ></v-text-field>
                          </v-list-item>
                          <v-list-item v-if="allOrganizationIds.length">
                            <v-checkbox
                              :input-value="allOrganizationsSelected"
                              :indeterminate="allOrganizationsIndeterminate"
                              :label="$t('carbonCredit.register.allOrganizations')"
                              :disabled="loadingOrganizations"
                              @change="toggleSelectAllOrganizations"
                            ></v-checkbox>
                          </v-list-item>
                          <v-divider v-if="allOrganizationIds.length"></v-divider>
                          <div v-for="org in organizations" :key="org.id">
                              <v-list-item>
                                  <v-checkbox
                                      v-model="selectedOrganizations"
                                      :label="org.name"
                                      :value="org.id"
                                      :indeterminate="isParentIndeterminate(org)"
                                      :disabled="loadingOrganizations"
                                      @change="() => toggleParentSelection(org)"
                                  ></v-checkbox>
                                  <v-spacer></v-spacer>
                                  <div
                                    v-if="org.subOrganizations && org.subOrganizations.length"
                                    class="d-flex align-center"
                                    style="cursor: pointer"
                                    :class="{ 'disabled': loadingOrganizations }"
                                    @click.stop="!loadingOrganizations && toggleExpand(org)"
                                  >
                                    <span class="text mr-2">
                                        {{ org.subOrganizations.length }} {{ $t('carbonCredit.projectInfo.subOrganizations') }}
                                    </span>
                                    <v-btn icon :disabled="loadingOrganizations">
                                        <v-icon>{{ org.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                    </v-btn>
                                  </div>
                              </v-list-item>
                              <v-expand-transition>
                                  <div v-if="org.expanded">
                                      <v-list-item
                                          v-for="subOrg in org.subOrganizations"
                                          :key="subOrg.id"
                                          class="ml-8"
                                      >
                                          <v-checkbox
                                              v-model="selectedOrganizations"
                                              :label="subOrg.name"
                                              :value="subOrg.id"
                                              :disabled="loadingOrganizations"
                                          ></v-checkbox>
                                      </v-list-item>
                                  </div>
                              </v-expand-transition>
                          </div>
                          <template v-if="loadingOrganizations">
                              <v-list-item class="justify-center">
                                  <div class="d-flex align-center">
                                      <v-progress-circular indeterminate color="primary" size="20" class="mr-2"></v-progress-circular>
                                      <span class="text-caption">{{ $t('carbonCredit.register.loadingOrganizations') }}</span>
                                  </div>
                              </v-list-item>
                          </template>
                          <template v-else-if="organizations.length === 0">
                              <v-list-item class="justify-center">
                                  <span class="text-caption text-muted">{{ $t('carbonCredit.register.noOrganizationsFound') }}</span>
                              </v-list-item>
                          </template>
                        </v-list>
                      </v-card>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="4">
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Project Info -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                {{ $t("carbonCredit.register.projectInfo") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mt-4" align="center">
                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.status")
                    }}</label>
                    <v-select v-model="form.status" :items="statuses" dense outlined
                      :placeholder="$t('carbonCredit.register.selectStatus')"
                      item-text="text"
                      item-value="value"></v-select>
                  </v-col>

                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.projectStage")
                    }}</label>
                    <v-select v-model="form.projectStage" :items="projectStages" dense outlined
                      :placeholder="$t('carbonCredit.register.selectProjectStage')"
                      item-text="text"
                      item-value="value"></v-select>
                  </v-col>

                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.creditType")
                    }}</label>
                    <v-select v-model="form.creditType" :items="creditTypes" dense outlined
                      :placeholder="$t('carbonCredit.register.selectCreditType')"
                      item-text="text"
                      item-value="value"></v-select>
                  </v-col>

                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.creditStart")
                    }}</label>
                    <v-text-field v-model="form.creditStart" dense outlined type="date"
                      :placeholder="$t('carbonCredit.register.dateFormat')"></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.creditEnd")
                    }}</label>
                    <v-text-field v-model="form.creditEnd" dense outlined type="date"
                      :placeholder="$t('carbonCredit.register.dateFormat')"></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.annualIssuance")
                    }}</label>
                    <v-text-field
                      v-model="form.annualIssuance"
                      dense
                      outlined
                      :placeholder="$t('carbonCredit.register.annualIssuancePlaceholder')"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.standardMethodology")
                    }}</label>
                    <v-text-field v-model="form.standard" dense outlined :placeholder="$t('carbonCredit.register.standardPlaceholder')"></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{ $t("carbonCredit.register.registerName") }}</label>
                    <v-text-field v-model="form.registerName" dense outlined :placeholder="$t('carbonCredit.register.registerName')"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{ $t("carbonCredit.register.partnerName") }}</label>
                    <v-text-field v-model="form.partnerName" dense outlined :placeholder="$t('carbonCredit.register.partnerName')"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{ $t("carbonCredit.register.validator") }}</label>
                    <v-text-field v-model="form.validator" dense outlined :placeholder="$t('carbonCredit.register.validator')"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{ $t("carbonCredit.register.registryUrl") }}</label>
                    <v-text-field v-model="form.registryUrl" dense outlined :placeholder="$t('carbonCredit.register.registryUrl')"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{ $t("carbonCredit.register.firstVerifier") }}</label>
                    <v-text-field v-model="form.firstVerifier" dense outlined :placeholder="$t('carbonCredit.register.firstVerifier')"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.validationDocumentation")
                    }}</label>
                    <v-file-input class="custom-file-input" v-model="form.validationDocumentation" prepend-icon=""
                      :placeholder="$t('carbonCredit.register.browse')" accept=".jpg,.png,.gif,.svg,.mp3,.wav,.aac,.ogg,.flac,.mp4,.mov,.webm,.mkv" solo
                      suffix="Supported file format JPG, PNG, GIF, SVG, Audio (MP3, WAV, AAC, OGG, FLAC), Video (MP4, MOV, WEBM, MKV)"></v-file-input>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Vintage -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                {{ $t("carbonCredit.register.vintage") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mt-4" align="center">
                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{ $t("carbonCredit.register.vintage") }}</label>
                    <v-select v-model="selectedVintageYears" :items="vintageYearOptions" dense outlined
                      :placeholder='$t("carbonCredit.register.selectVintage")' multiple chips :menu-props="{ maxHeight: '300px' }"
                      @change="onVintageYearsChange" />
                  </v-col>
                  <v-col cols="12" sm="6" md="6" lg="3">
                    <label class="font-weight-medium mb-1">{{ $t("carbonCredit.register.currency") }}</label>
                    <v-select v-model="form.currency" :items="currencies" dense outlined :placeholder="$t('carbonCredit.register.currencyPlaceholder')"></v-select>
                  </v-col>
                </v-row>
                <v-data-table :headers="vintageHeaders" :items="form.vintageRows" item-key="year" class="mt-6"
                  hide-default-footer>
                  <template v-slot:item.year="{ item }">
                    <v-text-field v-model="item.year" type="number" dense outlined hide-details disabled></v-text-field>
                  </template>
                  <template v-slot:item.credits="{ item }">
                    <v-text-field v-model="item.credits" type="number" dense outlined hide-details></v-text-field>
                  </template>
                  <template v-slot:item.price="{ item }">
                    <v-text-field v-model="item.price" type="number" dense outlined hide-details>
                      <template v-slot:append>
                        <span style="margin-left: 4px; margin-top: 2px; font-size: 15px; ">tCO2e</span>
                      </template>
                    </v-text-field>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn text color="error" @click="removeVintageYear(item.year)">{{ $t('carbonCredit.remove') }}</v-btn>
                  </template>
                </v-data-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Project Area (KML) -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                {{ $t("carbonCredit.register.kml.title") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="kml-section-intro my-4">
                  {{ $t("carbonCredit.register.kml.info") }}
                </p>

                <!-- KML/KMZ file -->
                <div class="kml-field-group mb-6">
                  <label class="font-weight-medium mb-1 d-block">
                    {{ $t("carbonCredit.register.kml.uploadLabel") }}
                  </label>
                  <div class="kml-upload-row">
                    <v-file-input
                      class="kml-file-input custom-file-input"
                      v-model="kmlFileInput"
                      prepend-icon=""
                      accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,application/xml,text/xml"
                      :placeholder="$t('carbonCredit.register.kml.browse')"
                      :loading="kmlLoading"
                      outlined
                      dense
                      hide-details
                      :clearable="false"
                      @change="onKmlFileChange"
                    />
                    <span v-if="hasKmlFile" class="kml-uploaded-status">
                      <v-icon small color="#00BD73">mdi-check-circle</v-icon>
                      {{ $t("carbonCredit.register.kml.uploaded") }}
                    </span>
                    <v-btn
                      v-if="hasKmlFile"
                      outlined
                      color="error"
                      class="kml-remove-btn"
                      :disabled="kmlLoading"
                      @click="removeKml"
                    >
                      {{ $t("carbonCredit.register.kml.remove") }}
                    </v-btn>
                  </div>
                  <span v-if="kmlParseError" class="error--text text-caption d-block mt-2">
                    {{ kmlParseError }}
                  </span>
                </div>

                <!-- Project area -->
                <div class="kml-field-group mb-4">
                  <label class="font-weight-medium mb-1 d-block">
                    {{ $t("carbonCredit.register.kml.areaLabel") }}
                  </label>
                  <div class="kml-area-row">
                    <v-text-field
                      v-model="form.projectArea"
                      type="number"
                      min="0"
                      step="0.0001"
                      outlined
                      dense
                      hide-details
                      class="kml-area-input"
                      :placeholder="$t('carbonCredit.register.kml.areaPlaceholder')"
                    />
                    <v-select
                      v-model="form.projectAreaUnit"
                      :items="kmlAreaUnitOptions"
                      item-text="label"
                      item-value="value"
                      outlined
                      dense
                      hide-details
                      class="kml-area-unit"
                      @change="onKmlAreaUnitChange"
                    />
                  </div>
                </div>

                <!-- Live interactive map (when polygons parsed) -->
                <div v-if="kmlPolygons.length || kmlMarkers.length" class="kml-map-wrapper" ref="kmlMapWrapper">
                  <GmapMap
                    ref="kmlMap"
                    :center="kmlMapCenter"
                    :zoom="kmlMapZoom"
                    map-type-id="satellite"
                    :options="kmlMapOptions"
                    style="width: 100%; height: 420px;"
                    @idle="onKmlMapIdle"
                  >
                    <GmapPolygon
                      v-for="(poly, idx) in kmlPolygons"
                      :key="`kml-poly-${idx}`"
                      :paths="poly"
                      :options="kmlPolygonOptions"
                    />
                    <GmapMarker
                      v-for="(pt, idx) in kmlMarkers"
                      :key="`kml-marker-${idx}`"
                      :position="pt"
                    />
                  </GmapMap>
                </div>

                <!-- Persisted snapshot already on file (edit mode, before re-upload) -->
                <div
                  v-else-if="kmlExistingPreviewUrl"
                  class="kml-saved-preview mt-2"
                >
                  <label class="font-weight-medium mb-1 d-block">
                    {{ $t("carbonCredit.register.kml.savedSnapshot") }}
                  </label>
                  <img
                    :src="kmlExistingPreviewUrl"
                    :alt="$t('carbonCredit.register.kml.title')"
                    class="kml-saved-image"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Attachment -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                {{ $t("carbonCredit.register.attachmentTitle") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="my-4">
                  {{ $t("carbonCredit.register.attachmentInfo") }}
                </p>
                <p class="text-caption gray-color mb-4">
                  {{ $t("carbonCredit.register.contractReviewDocumentsInfo") }}
                </p>

                <!-- File input for new uploads -->
                <v-row class="mb-4">
                  <v-col cols="6">
                    <label class="font-weight-medium mb-1">{{
                      $t("carbonCredit.register.documentation")
                    }}</label>
                    <v-file-input class="custom-file-input" v-model="form.attachmentFile" prepend-icon=""
                      :placeholder="$t('carbonCredit.register.browse')" accept=".jpg,.png,.gif,.mp3,.wav,.aac,.ogg,.flac,.mp4,.mov,.webm,.mkv,.kml,.shp,.pdf,.doc,.docx,.xls,.xlsx,.txt" solo
                      suffix="Supported file format JPG, PNG, GIF, Audio (MP3, WAV, AAC, OGG, FLAC), Video (MP4, MOV, WEBM, MKV), KML, SHP, PDF, DOC, XLS, etc."
                      multiple
                      @change="onAttachmentFilesPicked"
                    ></v-file-input>
                  </v-col>
                </v-row>

                <!-- Display existing attachments -->
                <v-row dense class="mt-4">
                  <v-col v-for="(file, index) in form.attachmentFile" :key="file.id || file.name || index" cols="12" sm="6" md="4" class="mb-4">
                    <div class="attachment-item-card pa-3" style="border: 1px solid #ddd; border-radius: 6px;">
                      <div class="d-flex align-start">
                        <div class="attachment-thumbnail mr-3" @click="openMediaModal(file)" style="
                            cursor: pointer;
                            width: 96px;
                            height: 80px;
                            flex: 0 0 auto;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border-radius: 6px;
                            border: 1px solid #ddd;
                            overflow: hidden;
                            position: relative;
                          ">
                          <img v-if="(file.file_type && file.file_type.startsWith('image')) || (file.type && file.type.startsWith('image'))"
                            :src="file.s3_url || getLocalFileUrl(file)" alt="Preview" style="width: 100%; height: 100%; object-fit: cover" />
                          <template v-else-if="file.type && file.type.startsWith('video')">
                            <video
                              :src="getLocalFileUrl(file)"
                              style="width: 100%; height: 100%; object-fit: cover; background: #000;"
                              muted playsinline preload="metadata"
                            ></video>
                            <v-icon
                              class="play-overlay"
                              style="position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); font-size: 24px; color: white; text-shadow: 0 0 8px #000; pointer-events: none;"
                            >mdi-play-circle</v-icon>
                          </template>
                          <v-icon v-else-if="(file.file_type && file.file_type.startsWith('video'))" style="font-size: 28px">mdi-play-circle</v-icon>
                          <div v-else-if="(file.file_type && file.file_type.startsWith('audio')) || (file.type && file.type.startsWith('audio'))" class="audio-thumbnail">
                            <div class="audio-waveform">
                              <div class="waveform-bars">
                                <div v-for="i in 20" :key="i" class="waveform-bar" :style="{ height: getRandomHeight() + '%' }"></div>
                              </div>
                            </div>
                            <div class="audio-info">
                              <v-icon class="audio-icon">mdi-microphone</v-icon>
                              <span class="audio-duration">{{ getAudioDuration(file) }}</span>
                            </div>
                          </div>
                          <span v-else class="text-caption text-truncate" style="padding: 4px">{{ file.file_name || file.name }}</span>
                        </div>
                        <div class="flex-grow-1" style="min-width: 0;">
                          <div class="d-flex align-center mb-1">
                            <v-btn icon x-small :disabled="index === 0" @click="moveAttachment(index, -1)" :title="$t('carbonCredit.register.moveUp')">
                              <v-icon small>mdi-arrow-up-bold</v-icon>
                            </v-btn>
                            <v-btn icon x-small :disabled="index === form.attachmentFile.length - 1" @click="moveAttachment(index, 1)" :title="$t('carbonCredit.register.moveDown')">
                              <v-icon small>mdi-arrow-down-bold</v-icon>
                            </v-btn>
                            <span class="text-caption ml-1 gray-color">{{ $t('carbonCredit.register.displayOrder') }}: {{ index + 1 }}</span>
                          </div>
                          <v-text-field
                            :value="getAttachmentMeta(index).title"
                            @input="setAttachmentMetaField(index, 'title', $event)"
                            dense
                            outlined
                            hide-details
                            class="mb-2"
                            :label="$t('carbonCredit.register.attachmentDocTitle')"
                          ></v-text-field>
                          <v-textarea
                            :value="getAttachmentMeta(index).description"
                            @input="setAttachmentMetaField(index, 'description', $event)"
                            dense
                            outlined
                            hide-details
                            rows="2"
                            auto-grow
                            class="mb-2"
                            :label="$t('carbonCredit.register.attachmentDocDescription')"
                          ></v-textarea>
                          <v-checkbox
                            :input-value="getAttachmentMeta(index).document_type === 'contract_review_doc'"
                            @change="onToggleContractReviewDoc(index, $event)"
                            dense
                            hide-details
                            :label="$t('carbonCredit.register.showToFarmerDuringContractReview')"
                          ></v-checkbox>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- SDGS -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                {{ $t("carbonCredit.register.sdgsTitle") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="my-4">
                  {{ $t("carbonCredit.register.sdgsInfo") }}
                </p>
                <v-row>
                  <v-col v-for="sdg in sdgs" :key="sdg.id" cols="12" md="6" lg="4" class="d-flex align-start mb-4">
                    <v-checkbox v-model="selectedSdgs" :value="sdg.id" class="mt-1 mr-3" hide-details />
                    <div class="d-flex flex-column">
                      <div class="d-flex align-center">
                        <img :src="sdg.icon" alt="sdg icon" height="40" width="40" class="mr-3"
                          style="border-radius: 4px" />
                        <span class="text-body-1">{{ sdg.title }}</span>
                      </div>
                      <v-expand-transition>
                        <div v-if="selectedSdgs.includes(sdg.id)" class="mt-2 ml-10">
                          <v-textarea :value="sdg.description" dense outlined auto-grow hide-details></v-textarea>
                        </div>
                      </v-expand-transition>
                    </div>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Data Collection Cycle -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                {{ $t("carbonCredit.register.dataCollectionTitle") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="my-4">
                  {{ $t("carbonCredit.register.dataCollectionInfo") }}
                </p>



                <v-data-table :headers="dataCycleHeaders" :items="dataCollectionModules" item-key="name"
                  class="elevation-1 data-collection-table" hide-default-footer>
                  <!-- Module Name with Checkbox -->
                  <template v-slot:item.name="{ item }">
                    <div class="d-flex align-center">
                      <v-checkbox v-model="item.enabled" class="mr-2" :input-value="item.enabled" @change="onModuleToggle(item)" />
                      <v-text-field 
                        :value="item.name" 
                        dense 
                        outlined 
                        disabled 
                        hide-details
                        class="module-name-field"
                        style="width: 120px"
                      ></v-text-field>
                    </div>
                  </template>

                  <!-- Mandatory Checkbox -->
                  <template v-slot:item.mandatory="{ item }">
                    <div class="d-flex align-center justify-center">
                      <v-checkbox v-model="item.mandatory" :disabled="!item.enabled" />
                    </div>
                  </template>

                  <!-- Frequency input + unit select -->
                  <template v-slot:item.recurring="{ item }">
                    <div class="d-flex align-center">
                      <v-row dense class="ma-0 pa-0" align="center" style="gap: 12px">
                        <v-text-field v-model="item.frequency" type="number" dense outlined hide-details
                          style="width: 60px" :disabled="!item.enabled"></v-text-field>
                        <v-select v-model="item.unit" :items="['Month(s)', 'Year(s)']" dense outlined hide-details
                          style="width: 100px" :disabled="!item.enabled"></v-select>
                      </v-row>
                    </div>
                  </template>

                  <!-- Score (editable) -->
                  <template v-slot:item.score="{ item }">
                    <div class="d-flex align-center">
                      <v-text-field 
                        v-model="item.score" 
                        type="number" 
                        dense 
                        outlined 
                        hide-details
                        :error-messages="getScoreError(item)"
                        :disabled="!item.enabled" 
                        placeholder="0"
                        min="0"
                        style="width: 80px; height: 40px;"
                        @input="validateScore(item)"
                        @blur="validateScore(item)"
                      ></v-text-field>
                    </div>
                  </template>

                  <!-- Approval (editable) -->
                  <template v-slot:item.approval="{ item }">
                    <div class="d-flex align-center">
                      <v-text-field 
                        v-model="item.approval" 
                        type="number" 
                        dense 
                        outlined 
                        hide-details
                        :error-messages="getApprovalError(item)"
                        :disabled="!item.enabled" 
                        placeholder="0" 
                        min="0" 
                        max="100"
                        style="width: 80px; height: 40px;"
                        @input="validateApproval(item)"
                        @blur="validateApproval(item)"
                      ></v-text-field>
                    </div>
                  </template>
                </v-data-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Footer -->
          <div class="d-flex justify-end mt-6">
            <v-btn outlined class="mr-3" @click="$router.go(-1)">{{
              $t("carbonCredit.cancel")
            }}</v-btn>
            <v-btn color="primary" @click="submit">{{
              projectId ? $t("carbonCredit.save") : $t("carbonCredit.submit")
            }}</v-btn>
          </div>
        </v-card>
      </v-form>
    </v-container>

    <MultimediaModal :show.sync="showMultimediaModal" :file="selectedMedia" :allFiles="form.attachmentFile"
      @close="showMultimediaModal = false" @update:media="selectedMedia = $event" />

    <AddDimitraOffice
      v-if="showAddDimitraOffice"
      :dialogI="showAddDimitraOffice"
      :organization-id="organizationId"
      @closeModal="showAddDimitraOffice = false"
      @officeCreated="onOfficeCreated"
    />

    <AddDimitraOffice
      v-if="showEditDimitraOffice"
      :dialogI="showEditDimitraOffice"
      :organization-id="organizationId"
      :office="editingDimitraOffice"
      mode="edit"
      @closeModal="showEditDimitraOffice = false"
      @officeUpdated="onOfficeUpdated"
    />
  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import CarbonCreditService from "../../../_services/CarbonCreditService";
import MultimediaModal from "./components/MultimediaModal.vue";
import { getCountries } from "country-state-picker";

import AddDimitraOffice from './components/AddDimitraOffice.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import JSZip from 'jszip';
import {
  isPlotTreeMappingApplicable,
  PROJECT_TYPE_I18N_KEYS,
} from '@/utils/carbonCreditProjectTypes';

export default {
  mixins: [loadingMixin],
  components: {
    MultimediaModal,
    AddDimitraOffice
  },
  data() {
    return {
      projectId: null,
      breadcrumbs: [
        { text: "Dashboard", disabled: false, href: "/dashboard", },
        { text: "Carbon Credit", disabled: false, href: "/carbon-credit/projects", },
        { text: "Projects", disabled: false, href: "/carbon-credit/projects", },
        { text: this.projectId ? this.$t('carbonCredit.editProject') : this.$t('carbonCredit.registerProject'), disabled: true, exact: true },
      ],
      form: {
        project_title: "",
        project_type: "",
        plot_tree_mapping_enabled: false,
        pin_verification_enabled: false,
        description: "",
        country: "",
        organization: "",
        status: "",
        projectStage: "planned",
        creditType: "",
        creditStart: "",
        creditEnd: "",
        annualIssuance: "",
        standard: "",
        registerName: "",
        partnerName: "",
        validator: "",
        registryUrl: "",
        firstVerifier: "",
        validationDocumentation: null,
        agreement: "",
        currency: "USD",
        vintageRows: [],
        attachmentFile: [],
        attachmentMetadata: [],
        dimitraOfficeId: null,
        projectArea: "",
        projectAreaUnit: "hectares",
      },
      uploadedDocuments: [],
      uploadedFileUrl: null,
      uploadedFile: null, // Store the actual File object
      headerImagePreviewUrl: null, // Store the preview URL (object URL or string URL)
      isUploading: false,
      dragging: false,
      vintages: [],
      currencies: ["USD", "EUR", "AUD"],
      dataCollectionModules: [],
      sdgs: [],
      selectedSdgs: [],
      countries: getCountries(),
      statuses: [
        { text: this.$t('carbonCredit.planned'), value: "planned" },
        { text: this.$t('carbonCredit.operational'), value: "operational" },
        { text: this.$t('carbonCredit.deactivated'), value: "deactivate" }
      ],
      projectTypeValues: [
        'forestry',
        'arr',
        'biochar',
        'erw',
        'grassland',
        'regenerative_agriculture',
        'agroforestry',
      ],
      projectStages: [
        { text: this.$t('carbonCredit.stages.planned'), value: "planned" },
        { text: this.$t('carbonCredit.stages.operational'), value: "operational" },
        { text: this.$t('carbonCredit.stages.inValidation'), value: "in_validation" },
        { text: this.$t('carbonCredit.stages.inVerification'), value: "in_verification" },
        { text: this.$t('carbonCredit.stages.creditsIssued'), value: "credits_issued" },
      ],
      creditTypes: [
        { text: this.$t('carbonCredit.register.removal'), value: "removal" },
        { text: this.$t('carbonCredit.register.avoided'), value: "avoided" }
      ],


      showMultimediaModal: false,
      selectedMedia: null,
      showAddDimitraOffice: false,
      showEditDimitraOffice: false,
      selectedVintageYears: [],
      selectedOrganizations: [],
      organizationSearchText: '',
      organizationMenu: false,
      updatingSelection: false,
      editingDimitraOffice: null,
      // KML upload + map snapshot state
      kmlFile: null,
      kmlFileInput: null,
      kmlPreviewFile: null, // generated PNG snapshot (File or Blob)
      kmlExistingFileUrl: null,
      kmlExistingPreviewUrl: null, 
      kmlPolygons: [], // array<array<{lat,lng}>> for GmapPolygon paths
      kmlMarkers: [], // array<{lat,lng}> standalone Point placemarks
      kmlMapCenter: { lat: 0, lng: 0 },
      kmlMapZoom: 4,
      kmlMapOptions: {
        streetViewControl: false,
        mapTypeControl: true,
        fullscreenControl: false,
        gestureHandling: 'cooperative',
        clickableIcons: false,
      },
      kmlPolygonOptions: {
        fillColor: '#00BD73',
        fillOpacity: 0.25,
        strokeColor: '#00BD73',
        strokeWeight: 3,
        clickable: false,
      },
      kmlRemoved: false,
      kmlLoading: false,
      kmlParseError: null,
      kmlAreaUnitOptions: [
        { label: "Hectare", value: "hectares" },
        { label: "Acres", value: "acres" },
      ],
    };
  },
  computed: {
    projectTypes() {
      return this.projectTypeValues.map((value) => {
        const key = PROJECT_TYPE_I18N_KEYS[value] || value;
        return {
          text: this.$t(`carbonCredit.${key}`),
          value,
        };
      });
    },
    plotTreeMappingApplicable() {
      return isPlotTreeMappingApplicable(this.form.project_type);
    },
    plotTreeMappingOptions() {
      return [
        {
          text: this.$t('carbonCredit.register.plotTreeMappingEnabled'),
          value: true,
        },
        {
          text: this.$t('carbonCredit.register.plotTreeMappingDisabled'),
          value: false,
        },
      ];
    },
    pinVerificationOptions() {
      return [
        {
          text: this.$t('carbonCredit.register.pinVerificationEnabled'),
          value: true,
        },
        {
          text: this.$t('carbonCredit.register.pinVerificationDisabled'),
          value: false,
        },
      ];
    },
    ...mapState('organization', {
        vuexOrganizations: state => state.organizations,
        vuexOrganizationSearch: state => state.organizationSearch,
    }),
    ...mapGetters('organization', [
        'organizations',
        'allOrganizations',
        'loadingOrganizations',
    ]),
    ...mapGetters('dimitraOffice', [
        'dimitraOffices',
        'loadingDimitraOffices'
    ]),
    hasKmlFile() {
      return !!(this.kmlFile || this.kmlExistingFileUrl);
    },
    vintageYearOptions() {
      const start = this.form.creditStart;
      const end = this.form.creditEnd;
      if (!start || !end) return [];

      const startYear = parseInt(String(start).substring(0, 4), 10);
      const endYear = parseInt(String(end).substring(0, 4), 10);
      if (isNaN(startYear) || isNaN(endYear) || startYear > endYear) return [];

      const years = [];
      for (let y = endYear; y >= startYear; y--) {
        years.push(String(y));
      }
      return years;
    },
   
    vintageHeaders() {
      return [
        { text: this.$t('carbonCredit.register.vintageYear'), value: "year" },
        { text: this.$t('carbonCredit.register.estimatedCredit'), value: "credits" },
        { text: this.$t('carbonCredit.register.pricePerCredit'), value: "price" },
        { text: "", value: "actions", sortable: false },
      ];
    },
    dataCycleHeaders() {
      return [
        { text: this.$t('carbonCredit.register.moduleName'), value: "name", width: "200px" },
        { text: this.$t('carbonCredit.register.mandatory'), value: "mandatory", width: "100px", align: "center" },
        { text: this.$t('carbonCredit.register.futureData'), value: "recurring", width: "250px" },
        { text: this.$t('carbonCredit.register.score'), value: "score", width: "100px", align: "center" },
        { text: this.$t('carbonCredit.register.approval'), value: "approval", width: "120px", align: "center" },
      ];
    },
    organizationId() {
      return this.$store.getters.getUser?.user_organization?.id;
    },
    organizationSearch: {
        get() {
            return this.organizationSearchText;
        },
        set(value) {
            this.organizationSearchText = value;
            this.setSearch(value || '');
        }
    },
    selectedOrganizationText() {
      if (this.selectedOrganizations.length === 0) return '';
      return `${this.selectedOrganizations.length} organizations selected`;
    },
    allOrganizationIds() {
      const ids = [];
      (this.allOrganizations || []).forEach((org) => {
        ids.push(org.id);
        (org.subOrganizations || []).forEach((s) => ids.push(s.id));
      });
      return ids;
    },
    allOrganizationsSelected() {
      const ids = this.allOrganizationIds;
      if (!ids.length) return false;
      const set = new Set(this.selectedOrganizations || []);
      return ids.every((id) => set.has(id));
    },
    allOrganizationsIndeterminate() {
      const ids = this.allOrganizationIds;
      if (!ids.length) return false;
      const set = new Set(this.selectedOrganizations || []);
      const selectedCount = ids.filter((id) => set.has(id)).length;
      return selectedCount > 0 && selectedCount < ids.length;
    },
    // Removed totalOrganizations since "All Organizations" option was removed

    // Removed allOrganizationsSelected since "All Organizations" option was removed
    headerImageUrl() {
      // Return preview URL if available, otherwise return the string URL
      if (this.headerImagePreviewUrl) {
        return this.headerImagePreviewUrl;
      }
      if (this.uploadedFileUrl && typeof this.uploadedFileUrl === 'string') {
        return this.uploadedFileUrl;
      }
      return null;
    },
  },
  async mounted() {
    this.projectId = this.$route.params.id || null;

    if (this.projectId) {
      // Restore edit draft when returning from View (without confirming)
      const draft = this.$store.getters['project/draftProject'];
      if (draft && draft.form && draft.projectId === this.projectId) {
        this.restoreDraft(draft);
        try {
          const sdgsRes = await CarbonCreditService.getAllSDGS();
          if (sdgsRes.success && Array.isArray(sdgsRes.data)) {
            this.sdgs = [...sdgsRes.data].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
          }
        } catch (err) {
          console.error('Failed to fetch SDGs', err);
        }
        this.setCountry('');
        if (this.organizationId) this.fetchDimitraOffices(this.organizationId);
        return;
      }
      try {
        this.startLoading();
        const res = await CarbonCreditService.getCarbonCreditProjectById(this.projectId);
        if (res.success && res.data) {
          const d = res.data;
          this.form.project_title = d.project_title;
          this.form.project_type = d.project_type;
          this.form.plot_tree_mapping_enabled = isPlotTreeMappingApplicable(d.project_type)
            ? !!d.plot_tree_mapping_enabled
            : false;
          this.form.pin_verification_enabled = !!d.pin_verification_enabled;
          this.form.description = d.description;
          this.form.status = d.status;
          this.form.projectStage = d.project_stage || "planned";
          this.form.creditType = d.credit_type;
          this.form.creditStart = d.credit_start_date?.split("T")[0];
          this.form.creditEnd = d.credit_end_date?.split("T")[0];
          this.form.annualIssuance = d.annual_issuance || "";
          this.form.country = d.country;
          this.form.standard = d.standard_methodology;
          this.form.registerName = d.register_name || "";
          this.form.partnerName = d.partner_name || "";
          this.form.validator = d.validator || "";
          this.form.registryUrl = d.registry_url || "";
          this.form.firstVerifier = d.first_verifier || "";
          // Handle validation documentation
          if (d.validation_documentation) {
            if (Array.isArray(d.validation_documentation) && d.validation_documentation.length > 0) {
              // Convert the first file object to a File-like object for v-file-input
              const firstFile = d.validation_documentation[0];
              this.form.validationDocumentation = {
                name: firstFile.file_name,
                type: firstFile.file_type || 'application/octet-stream',
                size: firstFile.file_size || 0,
                lastModified: new Date().getTime(),
                s3_url: firstFile.s3_url,
                id: firstFile.id
              };
            } else if (typeof d.validation_documentation === 'string') {
              // Convert string URL to File-like object
              const fileName = d.validation_documentation.split('/').pop();
              const fileType = fileName.split('.').pop();
              this.form.validationDocumentation = {
                name: fileName,
                type: fileType ? `application/${fileType}` : 'application/octet-stream',
                size: 0,
                lastModified: new Date().getTime(),
                s3_url: d.validation_documentation
              };
            } else {
              this.form.validationDocumentation = d.validation_documentation;
            }
          } else {
            this.form.validationDocumentation = null;
          }
          this.form.agreement = d.agreement;
          this.form.currency = d.vintage_currency;
          // Populate uploadedDocuments for existing agreement files
          if (d.agreement) {
            if (Array.isArray(d.agreement)) {
              this.uploadedDocuments = d.agreement;
            } else if (typeof d.agreement === 'string') {
              // If it's a string URL, create a file object for display
              this.uploadedDocuments = [{
                file_name: d.agreement.split('/').pop(),
                s3_url: d.agreement,
                file_type: d.agreement.split('.').pop(),
                name: d.agreement.split('/').pop() // Add name property for compatibility
              }];
            } else {
              // If it's a single object, wrap it in an array
              this.uploadedDocuments = [d.agreement];
            }
          } else {
            this.uploadedDocuments = [];
          }
          this.form.dimitraOfficeId = d.dimitra_office_id;
          this.form.projectArea =
            d.project_area != null && d.project_area !== ""
              ? String(d.project_area)
              : "";
          this.form.projectAreaUnit = d.project_area_unit || "hectares";
          // Handle header image - can be string URL from backend
          if (d.header_image) {
            this.uploadedFileUrl = d.header_image;
            this.headerImagePreviewUrl = d.header_image; // String URL for preview
            this.uploadedFile = null; // No file object when loading from backend
          } else {
            this.uploadedFileUrl = null;
            this.headerImagePreviewUrl = null;
            this.uploadedFile = null;
          }

          this.kmlExistingFileUrl = d.kml_file || null;
          this.kmlExistingPreviewUrl = d.kml_preview_image || null;
          this.kmlRemoved = false;
          if (d.kml_file) {
            this.tryRefreshKmlPolygonsFromUrl(d.kml_file);
          }

          this.form.vintageRows =
            d.vintages?.map((v) => ({
              year: v.vintage_year,
              credits: v.number_of_credits_estimated,
              price: v.price_per_credit,
            })) || [];

          // Populate selectedVintageYears from existing vintage rows
          if (this.form.vintageRows.length > 0) {
            this.selectedVintageYears = this.form.vintageRows.map(row => row.year.toString());
          }

          this.form.attachmentFile = d.attachments || [];
          this.form.attachmentMetadata = (d.attachments || []).map((a, i) => ({
            id: a.id != null ? a.id : null,
            title: a.title || '',
            description: a.description || '',
            document_type: a.document_type === 'contract_review_doc' ? 'contract_review_doc' : 'attachment',
            display_order: a.display_order != null ? a.display_order : i,
          }));

          this.selectedSdgs = d.sdgs?.map((s) => s.id) || [];

          // Initialize with predefined modules
          const predefinedModules = [
            { name: 'Sowing', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
            { name: 'Seeding', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
            { name: 'Fertilizers', enabled: false, mandatory: false, frequency: 1, unit: 'Month(s)', score: '', approval: '' },
            { name: 'Harvesting', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
            { name: 'Equipment', enabled: false, mandatory: false, frequency: 1, unit: 'Month(s)', score: '', approval: '' },
          ];

          // Merge with backend data
          if (d.modules && Array.isArray(d.modules)) {
            d.modules.forEach(backendModule => {
              const moduleName = backendModule.module_name.charAt(0).toUpperCase() + backendModule.module_name.slice(1);
              const existingModule = predefinedModules.find(m => m.name.toLowerCase() === moduleName.toLowerCase());
              
              if (existingModule) {
                existingModule.enabled = true;
                existingModule.mandatory = backendModule.mandatory ?? false;
                existingModule.frequency = backendModule.recurring_time || 1;
                existingModule.unit = backendModule.recurring_period === "M" ? "Month(s)" : "Year(s)";
                existingModule.score = backendModule.score?.toString() || '';
                existingModule.approval = backendModule.approval?.toString() || '';
              }
            });
          }

          this.dataCollectionModules = predefinedModules;

          this.selectedOrganizations = d.organizations?.map((o) => o.id) || [];
        }
      } catch (err) {
        console.error("Failed to load project:", err);
      } finally {
        this.stopLoading();
      }
    } else {
      // Restore draft when returning from View page (without confirming)
      const draft = this.$store.getters['project/draftProject'];
      if (draft && draft.form) {
        this.restoreDraft(draft);
      } else {
        this.dataCollectionModules = [
          { name: 'Sowing', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
          { name: 'Seeding', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
          { name: 'Fertilizers', enabled: false, mandatory: false, frequency: 1, unit: 'Month(s)', score: '', approval: '' },
          { name: 'Harvesting', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
          { name: 'Equipment', enabled: false, mandatory: false, frequency: 1, unit: 'Month(s)', score: '', approval: '' },
        ];
      }
    }

    try {
      const sdgsRes = await CarbonCreditService.getAllSDGS();
      if (sdgsRes.success && Array.isArray(sdgsRes.data)) {
        this.sdgs = [...sdgsRes.data].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
      }
    } catch (err) {
      console.error("Failed to fetch SDGs", err);
    }

    if (this.organizationId) {
      this.fetchDimitraOffices(this.organizationId);
    }
    this.setCountry('');
  },
  methods: {
    ...mapActions('organization', [
        'fetchOrganizations',
        'setSearch',
        'setCountry',
        'toggleOrgExpanded'
    ]),
    ...mapActions('dimitraOffice', [
        'fetchDimitraOffices',
    ]),
    handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      const files = event.dataTransfer.files;
      this.addFiles(files);
      this.dragging = false;
    },
    onFileChange(event) {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.addFiles(files);
        // Reset the input to allow selecting the same file again
        event.target.value = '';
      }
    },
    addFiles(files) {
      if (!files || files.length === 0) return;
      
      for (let file of files) {
        // Check if file is valid
        if (file && file.name) {
          this.uploadedDocuments.push(file);
        }
      }
      
      // Force Vue to re-render the component
      this.$forceUpdate();
    },
    getFileExtension(file) {
      const fileName = file.name || file.file_name;
      return fileName.split(".").pop().toUpperCase();
    },
    getFileName(file) {
      return file.name || file.file_name;
    },
    triggerAgreementFileInput() {
      this.$refs.agreementFileInput.click();
    },
    removeFile(index) {
      this.uploadedDocuments.splice(index, 1);
    },

    onAttachmentFilesPicked(files) {
      // v-file-input replaces the whole selection on every pick, so metadata is reset
      // to defaults in lockstep with the new (shorter or longer) file list.
      const list = files || [];
      this.form.attachmentMetadata = list.map((file, i) => ({
        id: null,
        title: '',
        description: '',
        document_type: 'attachment',
        display_order: i,
      }));
    },
    getAttachmentMeta(index) {
      return (
        this.form.attachmentMetadata[index] || {
          id: null,
          title: '',
          description: '',
          document_type: 'attachment',
          display_order: index,
        }
      );
    },
    ensureAttachmentMetadataLength() {
      const files = this.form.attachmentFile || [];
      const metas = this.form.attachmentMetadata || [];
      if (metas.length === files.length) return;
      this.form.attachmentMetadata = files.map((_, i) => metas[i] || {
        id: null,
        title: '',
        description: '',
        document_type: 'attachment',
        display_order: i,
      });
    },
    setAttachmentMetaField(index, field, value) {
      this.ensureAttachmentMetadataLength();
      this.$set(this.form.attachmentMetadata[index], field, value);
    },
    onToggleContractReviewDoc(index, checked) {
      this.ensureAttachmentMetadataLength();
      this.$set(
        this.form.attachmentMetadata[index],
        'document_type',
        checked ? 'contract_review_doc' : 'attachment'
      );
    },
    moveAttachment(index, delta) {
      const newIndex = index + delta;
      const files = this.form.attachmentFile || [];
      if (newIndex < 0 || newIndex >= files.length) return;
      this.ensureAttachmentMetadataLength();
      const metas = this.form.attachmentMetadata;

      const filesCopy = files.slice();
      const [movedFile] = filesCopy.splice(index, 1);
      filesCopy.splice(newIndex, 0, movedFile);

      const metasCopy = metas.slice();
      const [movedMeta] = metasCopy.splice(index, 1);
      metasCopy.splice(newIndex, 0, movedMeta);
      metasCopy.forEach((m, i) => { m.display_order = i; });

      this.form.attachmentFile = filesCopy;
      this.form.attachmentMetadata = metasCopy;
    },

    onProjectTypeChange() {
      if (!isPlotTreeMappingApplicable(this.form.project_type)) {
        this.form.plot_tree_mapping_enabled = false;
      } else if (
        this.form.plot_tree_mapping_enabled === null ||
        this.form.plot_tree_mapping_enabled === undefined
      ) {
        this.form.plot_tree_mapping_enabled = true;
      }
    },
    addVintageRow() {
      this.form.vintageRows.push({ year: "", credits: "", price: "" });
    },
    removeVintageYear(year) {
      this.selectedVintageYears = this.selectedVintageYears.filter((y) => y !== year);
      this.form.vintageRows = this.form.vintageRows.filter((row) => row.year !== year);
    },
    clampVintageYearsToCreditPeriod() {
      const start = this.form.creditStart;
      const end = this.form.creditEnd;
      if (!start || !end) return;
      const startYear = parseInt(String(start).substring(0, 4), 10);
      const endYear = parseInt(String(end).substring(0, 4), 10);
      if (isNaN(startYear) || isNaN(endYear) || startYear > endYear) return;

      const inRange = (y) => {
        const year = typeof y === 'number' ? y : parseInt(String(y), 10);
        return !isNaN(year) && year >= startYear && year <= endYear;
      };
      this.selectedVintageYears = this.selectedVintageYears.filter(inRange);
      this.form.vintageRows = this.form.vintageRows.filter((row) => inRange(row.year));
    },
    async submit() {
      try {
        // Check for negative price_per_credit
        const negativePrice = this.form.vintageRows.some(row => Number(row.price) < 0);
        if (negativePrice) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: this.$t('carbonCredit.register.pricePerCredit') + ' ' + this.$t('carbonCredit.register.errorNegative'),
            type: 'error'
          });
          return;
        }
        // Check for required fields
        const requiredFields = [
          { key: 'project_title', label: this.$t('carbonCredit.register.projectTitle') },
          { key: 'project_type', label: this.$t('carbonCredit.register.projectType') },
          { key: 'status', label: this.$t('carbonCredit.register.status') },
          { key: 'creditType', label: this.$t('carbonCredit.register.creditType') },
          { key: 'country', label: this.$t('carbonCredit.register.selectCountry') },
          { key: 'currency', label: this.$t('carbonCredit.register.currency') },
          { key: 'creditStart', label: this.$t('carbonCredit.register.creditStart') },
          { key: 'creditEnd', label: this.$t('carbonCredit.register.creditEnd') },
        ];
        for (const field of requiredFields) {
          if (!this.form[field.key] || this.form[field.key].toString().trim() === '') {
            this.$notify({
              title: this.$t('carbonCredit.error'),
              text: this.$t('carbonCredit.register.errorRequiredField', { field: field.label }),
              type: 'error'
            });
            return;
          }
        }
        // Require at least one organization selected
        if (!this.selectedOrganizations || !this.selectedOrganizations.length) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: this.$t('carbonCredit.register.errorOrganizationRequired'),
            type: 'error'
          });
          return;
        }
        // At least one vintage row and all fields in each row must be filled
        if (!this.form.vintageRows.length) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: this.$t('carbonCredit.register.errorVintageRequired'),
            type: 'error'
          });
          return;
        }
        for (const row of this.form.vintageRows) {
          if (!row.year || !row.credits || row.price === '' || row.price === null || row.price === undefined) {
            this.$notify({
              title: this.$t('carbonCredit.error'),
              text: this.$t('carbonCredit.register.errorVintageFieldsRequired'),
              type: 'error'
            });
            return;
          }
        }
        
        // Validate enabled modules
        const enabledModules = this.dataCollectionModules.filter(m => m.enabled);
        for (const module of enabledModules) {
          if (!this.validateScore(module)) {
            this.$notify({
              title: this.$t('carbonCredit.error'),
              text: this.$t('carbonCredit.register.scoreValidationError', { module: module.name }),
              type: 'error'
            });
            return;
          }
          
          if (!this.validateApproval(module)) {
            this.$notify({
              title: this.$t('carbonCredit.error'),
              text: this.$t('carbonCredit.register.approvalValidationError', { module: module.name }),
              type: 'error'
            });
            return;
          }
        }
        
        // Make sure we have a snapshot of the KML map (if a KML is loaded but no snapshot yet).
        if ((this.kmlFile || this.kmlPolygons.length || this.kmlMarkers.length) && !this.kmlPreviewFile) {
          try {
            await this.generateKmlSnapshot();
          } catch (snapErr) {
            console.warn('KML snapshot generation failed, continuing without snapshot.', snapErr);
          }
        }

        const kmlDraftPayload = {
          kmlFile: this.kmlFile || null,
          kmlPreviewFile: this.kmlPreviewFile || null,
          kmlExistingFileUrl: this.kmlExistingFileUrl || null,
          kmlExistingPreviewUrl: this.kmlExistingPreviewUrl || null,
          kmlRemoved: this.kmlRemoved,
          kmlPolygons: (this.kmlPolygons || []).map(poly => poly.map(p => ({ lat: p.lat, lng: p.lng }))),
          kmlMarkers: (this.kmlMarkers || []).map(p => ({ lat: p.lat, lng: p.lng })),
          kmlMapCenter: this.kmlMapCenter,
          kmlMapZoom: this.kmlMapZoom,
        };

        // New project: store draft and go to View; Confirm on View will submit to API
        if (!this.projectId) {
          const orgList = this.getSelectedOrganizationsList();
          const selectedOffice = this.form.dimitraOfficeId ? (this.dimitraOffices || []).find(o => o.id === this.form.dimitraOfficeId || o.id == this.form.dimitraOfficeId) : null;
          this.$store.dispatch('project/setDraftProject', {
            form: { ...this.form, vintageRows: (this.form.vintageRows || []).map(r => ({ ...r })) },
            uploadedDocuments: [...this.uploadedDocuments],
            uploadedFile: this.uploadedFile,
            uploadedFileUrl: this.uploadedFileUrl,
            selectedOrganizations: [...(this.selectedOrganizations || [])],
            selectedSdgs: [...(this.selectedSdgs || [])],
            dataCollectionModules: (this.dataCollectionModules || []).map(m => ({ ...m })),
            organizations: orgList,
            office: selectedOffice ? { ...selectedOffice } : null,
            ...kmlDraftPayload,
          });
          this.$router.push({ name: 'viewProject', params: { id: 'draft' }, query: { from: 'register' } });
          return;
        }

        // Edit: store draft and go to View; Confirm on View will submit update to API
        const orgList = this.getSelectedOrganizationsList();
        const selectedOffice = this.form.dimitraOfficeId ? (this.dimitraOffices || []).find(o => o.id === this.form.dimitraOfficeId || o.id == this.form.dimitraOfficeId) : null;
        this.$store.dispatch('project/setDraftProject', {
          projectId: this.projectId,
          form: { ...this.form, vintageRows: (this.form.vintageRows || []).map(r => ({ ...r })) },
          uploadedDocuments: [...this.uploadedDocuments],
          uploadedFile: this.uploadedFile,
          uploadedFileUrl: this.uploadedFileUrl,
          selectedOrganizations: [...(this.selectedOrganizations || [])],
          selectedSdgs: [...(this.selectedSdgs || [])],
          dataCollectionModules: (this.dataCollectionModules || []).map(m => ({ ...m })),
          organizations: orgList,
          office: selectedOffice ? { ...selectedOffice } : null,
          attachmentsRemoved: (this.attachmentsRemoved && this.attachmentsRemoved.length) ? [...this.attachmentsRemoved] : [],
          ...kmlDraftPayload,
        });
        this.$router.push({ name: 'viewProject', params: { id: this.projectId }, query: { from: 'edit' } });
      } catch (err) {
        this.$notify({
          title: this.$t('carbonCredit.error'),
          text: err.message || this.$t('carbonCredit.register.saveError'),
          type: 'error'
        });
      } finally {
        this.stopLoading();
      }
    },
    openMediaModal(file) {
      this.selectedMedia = file;
      this.showMultimediaModal = true;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      try {
        if (file) {
          if (!allowedTypes.includes(file.type)) {
            this.$notify({
              title: this.$t("carbonCredit.register.fileUploadFailed"),
              text: this.$t("carbonCredit.register.unsupportedFileFormat"),
              type: "error",
            });
            return;
          }
          
          // Clean up previous object URL if it exists
          if (this.headerImagePreviewUrl && this.headerImagePreviewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.headerImagePreviewUrl);
          }
          
          // Store the file object and create preview URL
          this.uploadedFile = file;
          this.headerImagePreviewUrl = URL.createObjectURL(file);
          this.uploadedFileUrl = file; // Keep for form submission
        }
      } catch (err) {
        this.$notify({
          title: this.$t("carbonCredit.register.fileUploadFailed"),
          text: this.$t("carbonCredit.register.errorUploadingFile"),
          type: "error",
        });
      }
    },

    deleteImage() {
      // Clean up object URL if it exists
      if (this.headerImagePreviewUrl && this.headerImagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.headerImagePreviewUrl);
      }
      this.uploadedFileUrl = null;
      this.uploadedFile = null;
      this.headerImagePreviewUrl = null;
    },
    handleNewDimitraOffice() {
      this.showAddDimitraOffice = true;
    },
    onVintageYearsChange(newYears) {
      // Create a map of existing vintage data to preserve it
      const existingVintageData = {};
      this.form.vintageRows.forEach(row => {
        existingVintageData[row.year] = {
          credits: row.credits,
          price: row.price
        };
      });

      // Clear and rebuild vintage rows
      this.form.vintageRows = [];
      
      // Add years with preserved data
      newYears.forEach((year) => {
        const existingData = existingVintageData[year] || { credits: '', price: '' };
        this.form.vintageRows.push({ 
          year, 
          credits: existingData.credits, 
          price: existingData.price 
        });
      });
    },
    onModuleToggle(item) {
      if (!item.enabled) {
        item.mandatory = false;
        item.frequency = 1;
        item.unit = "Year(s)";
        item.score = '';
        item.approval = '';
        // Clear validation errors
        if (item.scoreError) delete item.scoreError;
        if (item.approvalError) delete item.approvalError;
      }
    },
    onOfficeCreated() {
      // Fetch the updated list of offices so the dropdown is refreshed
      if (this.organizationId) {
        this.fetchDimitraOffices(this.organizationId);
      }
      this.showAddDimitraOffice = false;
    },
    onEditDimitraOffice() {
      const selectedId = this.form.dimitraOfficeId;
      if (!selectedId) return;

      const office = (this.dimitraOffices || []).find(o => o.id === selectedId);
      if (!office) return;

      this.editingDimitraOffice = { ...office };
      this.showEditDimitraOffice = true;
    },
    onOfficeUpdated(updatedOffice) {
      // Refresh offices from backend to keep Vuex state in sync
      if (this.organizationId) {
        this.fetchDimitraOffices(this.organizationId);
      }
      if (updatedOffice && updatedOffice.id) {
        this.form.dimitraOfficeId = updatedOffice.id;
      }
      this.showEditDimitraOffice = false;
    },
    getSelectedOrganizationsList() {
      const ids = new Set(this.selectedOrganizations || []);
      const list = [];
      (this.allOrganizations || []).forEach(org => {
        if (ids.has(org.id)) {
          list.push({ id: org.id, name: org.name, address: org.address || '', country: org.country || '', website: org.website || '' });
        }
        (org.subOrganizations || []).forEach(s => {
          if (ids.has(s.id)) {
            list.push({ id: s.id, name: s.name, address: s.address || '', country: s.country || '', website: s.website || '' });
          }
        });
      });
      return list;
    },
    restoreDraft(draft) {
      const f = draft.form;
      this.form.project_title = f.project_title || '';
      this.form.project_type = f.project_type || '';
      this.form.plot_tree_mapping_enabled = isPlotTreeMappingApplicable(this.form.project_type)
        ? !!f.plot_tree_mapping_enabled
        : false;
      this.form.pin_verification_enabled = !!f.pin_verification_enabled;
      this.form.description = f.description || '';
      this.form.country = f.country || '';
      this.form.status = f.status || '';
      this.form.projectStage = f.projectStage || 'planned';
      this.form.creditType = f.creditType || '';
      this.form.creditStart = f.creditStart || '';
      this.form.creditEnd = f.creditEnd || '';
      this.form.annualIssuance = f.annualIssuance || '';
      this.form.standard = f.standard || '';
      this.form.registerName = f.registerName || '';
      this.form.partnerName = f.partnerName || '';
      this.form.validator = f.validator || '';
      this.form.registryUrl = f.registryUrl || '';
      this.form.firstVerifier = f.firstVerifier || '';
      this.form.currency = f.currency || 'USD';
      this.form.dimitraOfficeId = f.dimitraOfficeId != null ? f.dimitraOfficeId : null;
      this.form.vintageRows = (f.vintageRows || []).map(r => ({ year: r.year, credits: r.credits, price: r.price }));
      this.selectedVintageYears = this.form.vintageRows.map(row => String(row.year));
      this.form.attachmentFile = (f.attachmentFile || []).slice();
      this.form.attachmentMetadata = (f.attachmentMetadata || []).map(m => ({ ...m }));
      this.form.validationDocumentation = f.validationDocumentation || null;

      this.uploadedDocuments = (draft.uploadedDocuments || []).slice();
      this.uploadedFile = draft.uploadedFile || null;
      this.uploadedFileUrl = draft.uploadedFileUrl || null;
      if (draft.uploadedFile) {
        this.headerImagePreviewUrl = URL.createObjectURL(draft.uploadedFile);
      } else if (draft.uploadedFileUrl && typeof draft.uploadedFileUrl === 'string') {
        this.headerImagePreviewUrl = draft.uploadedFileUrl;
      } else {
        this.headerImagePreviewUrl = null;
      }

      this.selectedOrganizations = (draft.selectedOrganizations || []).slice();
      this.selectedSdgs = (draft.selectedSdgs || []).slice();

      this.kmlFile = draft.kmlFile || null;
      this.kmlFileInput = draft.kmlFile || null;
      this.kmlPreviewFile = draft.kmlPreviewFile || null;
      this.kmlExistingFileUrl = draft.kmlExistingFileUrl || null;
      this.kmlExistingPreviewUrl = draft.kmlExistingPreviewUrl || null;
      this.kmlRemoved = !!draft.kmlRemoved;
      this.kmlPolygons = (draft.kmlPolygons || []).map(poly => poly.map(p => ({ lat: p.lat, lng: p.lng })));
      this.kmlMarkers = (draft.kmlMarkers || []).map(p => ({ lat: p.lat, lng: p.lng }));
      if (draft.kmlMapCenter && typeof draft.kmlMapCenter.lat === 'number') {
        this.kmlMapCenter = { lat: draft.kmlMapCenter.lat, lng: draft.kmlMapCenter.lng };
        this.kmlMapZoom = draft.kmlMapZoom || this.kmlMapZoom;
      }
      // If we restored an existing KML URL but no parsed polygons, re-fetch + parse.
      if (this.kmlExistingFileUrl && !this.kmlPolygons.length && !this.kmlMarkers.length) {
        this.tryRefreshKmlPolygonsFromUrl(this.kmlExistingFileUrl);
      }

      const predefinedModules = [
        { name: 'Sowing', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
        { name: 'Seeding', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
        { name: 'Fertilizers', enabled: false, mandatory: false, frequency: 1, unit: 'Month(s)', score: '', approval: '' },
        { name: 'Harvesting', enabled: false, mandatory: false, frequency: 1, unit: 'Year(s)', score: '', approval: '' },
        { name: 'Equipment', enabled: false, mandatory: false, frequency: 1, unit: 'Month(s)', score: '', approval: '' },
      ];
      (draft.dataCollectionModules || []).forEach(dm => {
        const m = predefinedModules.find(p => p.name.toLowerCase() === (dm.name || '').toLowerCase());
        if (m) {
          m.enabled = true;
          m.mandatory = dm.mandatory || false;
          m.frequency = dm.frequency ?? 1;
          m.unit = dm.unit || 'Year(s)';
          m.score = dm.score !== undefined && dm.score !== null ? String(dm.score) : '';
          m.approval = dm.approval !== undefined && dm.approval !== null ? String(dm.approval) : '';
        }
      });
      this.dataCollectionModules = predefinedModules;
    },
    onCountryChange() {
      // Country is form-only; do not filter organizations by country
    },
    toggleExpand(org) {
        this.toggleOrgExpanded(org);
    },
    toggleSelectAllOrganizations() {
      if (this.allOrganizationsSelected) {
        this.selectedOrganizations = [];
      } else {
        this.selectedOrganizations = [...this.allOrganizationIds];
      }
    },
    toggleParentSelection(org) {
      this.updatingSelection = true;
      const subOrgIds = (org.subOrganizations || []).map(s => s.id);
      const parentId = org.id;
      
      setTimeout(() => {
        const isParentNowSelected = this.selectedOrganizations.includes(parentId);
        
        if (isParentNowSelected) {
          subOrgIds.forEach(id => {
            if (!this.selectedOrganizations.includes(id)) {
              this.selectedOrganizations.push(id);
            }
          });
        } else {
          this.selectedOrganizations = this.selectedOrganizations.filter(
            id => !subOrgIds.includes(id)
          );
        }
        this.$nextTick(() => {
          this.updatingSelection = false;
        });
      }, 0);
    },
    isParentIndeterminate(org) {
      const subOrgIds = (org.subOrganizations || []).map(s => s.id);
      if (subOrgIds.length === 0) return false;

      const selectedSubCount = subOrgIds.filter(id => this.selectedOrganizations.includes(id)).length;
      
      return selectedSubCount > 0 && selectedSubCount < subOrgIds.length;
    },
    getLocalFileUrl(file) {
      if (file instanceof File) {
        if (!file._localUrl) {
          file._localUrl = URL.createObjectURL(file);
        }
        return file._localUrl;
      }
      return null;
    },
    validateScore(item) {
      const score = parseFloat(item.score);
      
      // Clear previous validation state
      if (item.scoreError) {
        delete item.scoreError;
      }
      
      // Check if value is empty (allow empty for optional fields)
      if (item.score === '' || item.score === null || item.score === undefined) {
        return true;
      }
      
      // Check if it's a valid number
      if (isNaN(score)) {
        item.scoreError = this.$t('carbonCredit.register.scoreError.invalid');
        return false;
      }
      
      // Check range (0 and above)
      if (score < 0) {
        item.scoreError = this.$t('carbonCredit.register.scoreError.range');
        return false;
      }
      
      // Check if it's a whole number
      if (!Number.isInteger(score)) {
        item.scoreError = this.$t('carbonCredit.register.scoreError.wholeNumber');
        return false;
      }
      
      return true;
    },
    validateApproval(item) {
      const approval = parseFloat(item.approval);
      
      // Clear previous validation state
      if (item.approvalError) {
        delete item.approvalError;
      }
      
      // Check if value is empty (allow empty for optional fields)
      if (item.approval === '' || item.approval === null || item.approval === undefined) {
        return true;
      }
      
      // Check if it's a valid number
      if (isNaN(approval)) {
        item.approvalError = this.$t('carbonCredit.register.approvalError.invalid');
        return false;
      }
      
      // Check range (0-100)
      if (approval < 0 || approval > 100) {
        item.approvalError = this.$t('carbonCredit.register.approvalError.range');
        return false;
      }
      
      // Check if it's a whole number
      if (!Number.isInteger(approval)) {
        item.approvalError = this.$t('carbonCredit.register.approvalError.wholeNumber');
        return false;
      }
      
      return true;
    },
    getScoreError(item) {
      return item.scoreError || '';
    },
    getApprovalError(item) {
      return item.approvalError || '';
    },
    getRandomHeight() {
      return Math.floor(Math.random() * 60) + 20; // Random height between 20% and 80%
    },
    async onKmlFileChange(fileOrEvent) {
      // v-file-input passes the File directly (or null when cleared).
      const file = fileOrEvent && fileOrEvent.target
        ? (fileOrEvent.target.files && fileOrEvent.target.files[0])
        : fileOrEvent;
      if (!file) {
        return;
      }
      this.kmlParseError = null;
      this.kmlLoading = true;
      try {
        const text = await this.readKmlContentFromFile(file);
        const { polygons, markers, center, zoom } = this.parseKmlText(text);
        if (!polygons.length && !markers.length) {
          this.kmlParseError = this.$t('carbonCredit.register.kml.parseError');
          return;
        }
        this.kmlFile = file;
        this.kmlPolygons = polygons;
        this.kmlMarkers = markers;
        if (center) {
          this.kmlMapCenter = center;
          this.kmlMapZoom = zoom;
        }
        // New KML invalidates any previously generated/persisted snapshot;
        // it will be regenerated lazily before submit.
        this.kmlPreviewFile = null;
        this.kmlExistingPreviewUrl = null;
        this.kmlRemoved = false;
        this._kmlMapFitted = false;
        this.updateProjectAreaFromKml();
      } catch (err) {
        console.error('KML parse failed', err);
        this.kmlParseError = this.$t('carbonCredit.register.kml.parseError');
      } finally {
        this.kmlLoading = false;
      }
    },
    isKmzFile(file) {
      if (!file) return false;
      const name = (file.name || '').toLowerCase();
      const type = (file.type || '').toLowerCase();
      return (
        name.endsWith('.kmz') ||
        type === 'application/vnd.google-earth.kmz' ||
        type === 'application/zip'
      );
    },
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
      });
    },
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
        reader.readAsText(file);
      });
    },
    async extractKmlTextFromKmzBuffer(buffer) {
      const zip = await JSZip.loadAsync(buffer);
      const kmlPaths = Object.keys(zip.files).filter((path) => {
        const entry = zip.files[path];
        return entry && !entry.dir && /\.kml$/i.test(path);
      });
      if (!kmlPaths.length) {
        throw new Error('No KML document found inside KMZ archive');
      }
      const docPath =
        kmlPaths.find((p) => /(^|\/)doc\.kml$/i.test(p)) ||
        kmlPaths.sort((a, b) => a.length - b.length)[0];
      return zip.file(docPath).async('string');
    },
    async readKmlContentFromFile(file) {
      if (this.isKmzFile(file)) {
        const buffer = await this.readFileAsArrayBuffer(file);
        return this.extractKmlTextFromKmzBuffer(buffer);
      }
      return this.readFileAsText(file);
    },
    parseKmlText(text) {
      const parser = new DOMParser();
      const xml = parser.parseFromString(text, 'application/xml');
      if (xml.getElementsByTagName('parsererror').length) {
        throw new Error('Invalid KML XML');
      }

      const parseCoordString = (raw) => raw
        .split(/\s+/)
        .map(c => c.trim())
        .filter(Boolean)
        .map(triple => {
          const parts = triple.split(',');
          const lng = parseFloat(parts[0]);
          const lat = parseFloat(parts[1]);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { lat, lng };
        })
        .filter(Boolean);

      const polygons = [];
      const addPolygonCoords = (coordEl) => {
        if (!coordEl) return;
        const coords = parseCoordString(coordEl.textContent || '');
        if (coords.length >= 3) polygons.push(coords);
      };

      const polyNodes = xml.getElementsByTagName('Polygon');
      for (let i = 0; i < polyNodes.length; i++) {
        const outer = polyNodes[i].getElementsByTagName('outerBoundaryIs')[0];
        if (outer) {
          addPolygonCoords(outer.getElementsByTagName('coordinates')[0]);
          continue;
        }
        addPolygonCoords(polyNodes[i].getElementsByTagName('coordinates')[0]);
      }

      const ringNodes = xml.getElementsByTagName('LinearRing');
      for (let i = 0; i < ringNodes.length; i++) {
        addPolygonCoords(ringNodes[i].getElementsByTagName('coordinates')[0]);
      }

      // Also pick up LineStrings (treat as open polylines, render as polygon path).
      const lineNodes = xml.getElementsByTagName('LineString');
      for (let i = 0; i < lineNodes.length; i++) {
        const coordEl = lineNodes[i].getElementsByTagName('coordinates')[0];
        if (!coordEl) continue;
        const coords = parseCoordString(coordEl.textContent || '');
        if (coords.length >= 2) polygons.push(coords);
      }

      const markers = [];
      const pointNodes = xml.getElementsByTagName('Point');
      for (let i = 0; i < pointNodes.length; i++) {
        const coordEl = pointNodes[i].getElementsByTagName('coordinates')[0];
        if (!coordEl) continue;
        const coords = parseCoordString(coordEl.textContent || '');
        if (coords.length) markers.push(coords[0]);
      }

      // Compute center + a rough zoom from bounding box.
      const allPoints = [
        ...polygons.flat(),
        ...markers,
      ];
      let center = null;
      let zoom = 4;
      if (allPoints.length) {
        let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
        allPoints.forEach(p => {
          if (p.lat < minLat) minLat = p.lat;
          if (p.lat > maxLat) maxLat = p.lat;
          if (p.lng < minLng) minLng = p.lng;
          if (p.lng > maxLng) maxLng = p.lng;
        });
        center = { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
        const span = Math.max(maxLat - minLat, maxLng - minLng);
        if (span < 0.001) zoom = 17;
        else if (span < 0.01) zoom = 15;
        else if (span < 0.1) zoom = 12;
        else if (span < 1) zoom = 9;
        else if (span < 5) zoom = 7;
        else if (span < 20) zoom = 5;
        else zoom = 3;
      }

      return { polygons, markers, center, zoom };
    },
    async tryRefreshKmlPolygonsFromUrl(url) {
      if (!url) return;
      try {
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) return;
        const isKmz = /\.kmz(\?|$)/i.test(url);
        const text = isKmz
          ? await this.extractKmlTextFromKmzBuffer(await res.arrayBuffer())
          : await res.text();
        const { polygons, markers, center, zoom } = this.parseKmlText(text);
        if (polygons.length || markers.length) {
          this.kmlPolygons = polygons;
          this.kmlMarkers = markers;
          if (center) {
            this.kmlMapCenter = center;
            this.kmlMapZoom = zoom;
          }
          this._kmlMapFitted = false;
        }
      } catch (err) {
        // Cross-origin/network issues are non-fatal — the saved snapshot still
        // renders even if we couldn't re-parse polygons for the live preview.
        console.warn('Could not re-fetch stored KML for live preview:', err);
      }
    },
    removeKml() {
      this.kmlFile = null;
      this.kmlFileInput = null;
      this.kmlPreviewFile = null;
      this.kmlPolygons = [];
      this.kmlMarkers = [];
      this.kmlExistingFileUrl = null;
      this.kmlExistingPreviewUrl = null;
      this.kmlRemoved = true;
      this.kmlParseError = null;
      this.form.projectArea = "";
      this.form.projectAreaUnit = "hectares";
    },
    toRad(deg) {
      return (deg * Math.PI) / 180;
    },
    computePolygonAreaSqMeters(coords) {
      if (!coords || coords.length < 3) return 0;
      const R = 6378137;
      let area = 0;
      const pts =
        coords[0].lat === coords[coords.length - 1].lat &&
        coords[0].lng === coords[coords.length - 1].lng
          ? coords
          : [...coords, coords[0]];
      for (let i = 0; i < pts.length - 1; i++) {
        const p1 = pts[i];
        const p2 = pts[i + 1];
        area +=
          this.toRad(p2.lng - p1.lng) *
          (2 + Math.sin(this.toRad(p1.lat)) + Math.sin(this.toRad(p2.lat)));
      }
      return Math.abs((area * R * R) / 2);
    },
    computeKmlTotalAreaSqMeters() {
      return (this.kmlPolygons || []).reduce((sum, poly) => {
        if (!poly || poly.length < 3) return sum;
        return sum + this.computePolygonAreaSqMeters(poly);
      }, 0);
    },
    sqMetersToAreaUnit(sqM, unit) {
      if (unit === "acres") return sqM / 4046.8564224;
      return sqM / 10000;
    },
    updateProjectAreaFromKml() {
      const sqM = this.computeKmlTotalAreaSqMeters();
      if (sqM <= 0) return;
      const value = this.sqMetersToAreaUnit(sqM, this.form.projectAreaUnit);
      this.form.projectArea = Number(value.toFixed(4)).toString();
    },
    onKmlAreaUnitChange() {
      if (this.kmlPolygons.length) {
        this.updateProjectAreaFromKml();
      }
    },
    onKmlMapIdle() {
      // The map sometimes lays out with stale bounds when polygons are added
      // after first render. Re-fit to the polygon bounds once tiles are settled.
      try {
        const mapRef = this.$refs.kmlMap;
        if (!mapRef || !mapRef.$mapObject) return;
        if (!this.kmlPolygons.length && !this.kmlMarkers.length) return;
        if (this._kmlMapFitted) return;
        const google = window.google;
        if (!google || !google.maps) return;
        const bounds = new google.maps.LatLngBounds();
        this.kmlPolygons.forEach(poly => poly.forEach(p => bounds.extend(p)));
        this.kmlMarkers.forEach(p => bounds.extend(p));
        if (!bounds.isEmpty()) {
          mapRef.$mapObject.fitBounds(bounds, 40);
          this._kmlMapFitted = true;
        }
      } catch (err) {
        // map not ready yet — ignore
      }
    },
    async generateKmlSnapshot() {
      if (!this.kmlPolygons.length && !this.kmlMarkers.length) return;
      const allPoints = [
        ...this.kmlPolygons.flat(),
        ...this.kmlMarkers,
      ];
      if (!allPoints.length) return;

      const apiKey = process.env.VUE_APP_GOOGLE_MAP_KEY;
      const size = '720x420';

      // Prefer the Google Static Maps API (gives a real satellite snapshot with the polygon drawn).
      if (apiKey) {
        try {
          const blob = await this.fetchStaticMapImage(apiKey, size);
          if (blob) {
            this.kmlPreviewFile = new File([blob], 'kml-preview.png', { type: 'image/png' });
            return;
          }
        } catch (err) {
          console.warn('Static Maps snapshot failed, falling back to canvas snapshot:', err);
        }
      }

      // Fallback: draw the polygons onto a plain canvas (no satellite tiles, no API key required).
      const blob = await this.drawKmlOnCanvas(720, 420);
      if (blob) {
        this.kmlPreviewFile = new File([blob], 'kml-preview.png', { type: 'image/png' });
      }
    },
    async fetchStaticMapImage(apiKey, size) {
      const polygonPaths = this.kmlPolygons.map(poly => {
        const points = poly.map(p => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`).join('|');
        return `path=color:0x00BD73ff|weight:3|fillcolor:0x00BD7340|${points}`;
      });
      const markerParam = this.kmlMarkers.length
        ? 'markers=color:red|' + this.kmlMarkers.map(p => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`).join('|')
        : '';
      const params = [
        `size=${size}`,
        'maptype=satellite',
        ...polygonPaths,
        markerParam,
        `key=${apiKey}`,
      ].filter(Boolean);
      const url = `https://maps.googleapis.com/maps/api/staticmap?${params.join('&')}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Static Maps responded with ${res.status}`);
      }
      return await res.blob();
    },
    async drawKmlOnCanvas(width, height) {
      // Project lat/lng to canvas coords using a simple linear mapping over the
      // bounding box. Good enough for a recognisable shape preview.
      const allPoints = [...this.kmlPolygons.flat(), ...this.kmlMarkers];
      if (!allPoints.length) return null;
      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      allPoints.forEach(p => {
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
      });
      // Pad bounds slightly so the geometry isn't flush with the edge.
      const padFrac = 0.1;
      const latPad = Math.max((maxLat - minLat) * padFrac, 1e-6);
      const lngPad = Math.max((maxLng - minLng) * padFrac, 1e-6);
      minLat -= latPad; maxLat += latPad;
      minLng -= lngPad; maxLng += lngPad;
      const latRange = maxLat - minLat || 1e-6;
      const lngRange = maxLng - minLng || 1e-6;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, width, height);

      const project = (p) => ({
        x: ((p.lng - minLng) / lngRange) * width,
        y: height - ((p.lat - minLat) / latRange) * height,
      });

      ctx.lineWidth = 3;
      ctx.strokeStyle = '#00BD73';
      ctx.fillStyle = 'rgba(0, 189, 115, 0.25)';
      this.kmlPolygons.forEach(poly => {
        if (poly.length < 2) return;
        ctx.beginPath();
        poly.forEach((p, idx) => {
          const xy = project(p);
          if (idx === 0) ctx.moveTo(xy.x, xy.y);
          else ctx.lineTo(xy.x, xy.y);
        });
        if (poly.length >= 3) ctx.closePath();
        if (poly.length >= 3) ctx.fill();
        ctx.stroke();
      });

      ctx.fillStyle = '#d32f2f';
      this.kmlMarkers.forEach(p => {
        const xy = project(p);
        ctx.beginPath();
        ctx.arc(xy.x, xy.y, 6, 0, Math.PI * 2);
        ctx.fill();
      });

      return await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    },
    getAudioDuration(file) {
      // Return cached duration if available
      if (file._audioDuration) {
        return file._audioDuration;
      }
      
      // For new files, extract duration
      if (file instanceof File && file.type.startsWith('audio')) {
        const audio = new Audio();
        audio.src = URL.createObjectURL(file);
        
        audio.addEventListener('loadedmetadata', () => {
          const duration = audio.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          file._audioDuration = `${minutes}.${seconds.toString().padStart(2, '0')}`;
          this.$forceUpdate(); // Force re-render to show the duration
        });
        
        // Return placeholder while loading
        return '0.00';
      }
      
      // For existing files from backend, try to extract from metadata
      if (file.duration) {
        const minutes = Math.floor(file.duration / 60);
        const seconds = Math.floor(file.duration % 60);
        return `${minutes}.${seconds.toString().padStart(2, '0')}`;
      }
      
      // Fallback for files without duration info
      return '0.00';
    },

  },
  beforeDestroy() {
    // Clean up object URLs to prevent memory leaks
    if (this.headerImagePreviewUrl && this.headerImagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.headerImagePreviewUrl);
    }
  },
  watch: {
    selectedOrganizations(newVal) {
      if (this.updatingSelection) return;
      this.updatingSelection = true;
      
      this.$nextTick(() => {
        this.vuexOrganizations.forEach(org => {
          const subOrgIds = (org.subOrganizations || []).map(s => s.id);
          if (subOrgIds.length === 0) return;

          const selectedSubCount = subOrgIds.filter(id => newVal.includes(id)).length;
          const parentSelected = newVal.includes(org.id);

          if (selectedSubCount === subOrgIds.length) {
            if (!parentSelected) {
              this.selectedOrganizations.push(org.id);
            }
          } else {
            if (parentSelected) {
              const index = this.selectedOrganizations.indexOf(org.id);
              if (index !== -1) {
                this.selectedOrganizations.splice(index, 1);
              }
            }
          }
        });
        this.$nextTick(() => {
          this.updatingSelection = false;
        });
      });
    },
    'form.organization'() {
      this.fetchDimitraOffices();
    },
    organizationId(newId) {
      if (newId) {
        this.fetchDimitraOffices(newId);
      }
    },
    'form.creditStart'() {
      this.clampVintageYearsToCreditPeriod();
    },
    'form.creditEnd'() {
      this.clampVintageYearsToCreditPeriod();
    },
  },
};
</script>

<style lang="scss" scoped>
.v-expansion-panel-header {
  background-color: #fff7e6 !important;
  font-weight: bold;
  font-size: 16px;
}

.bordered-expansion {
  border: 1px solid #ffb443;
  border-radius: 6px;
  background-color: #ffb443;
}

.upload-document {
  width: 100%;
}

.dropZone {
  width: 720px;
  height: 200px;
  position: relative;
  border: 2px dashed #97a8b8;
}

.dropZone:hover {
  border: 2px solid $primary-color;
}

.dropZone:hover .dropZone-title {
  color: $primary-color;
}

.dropZone-info {
  color: #a8a8a8;
  position: absolute;
  top: 50%;
  width: 100%;
  transform: translate(0, -50%);
  text-align: center;
  font-size: 12px;

  .underline {
    text-decoration: underline;
    color: #0057bd;
  }

  .blue-color {
    color: #0057bd;
  }
}

.dropZone-title {
  color: #787878;
}

.dropZone input {
  position: absolute;
  cursor: pointer;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
}

.dropZone-over {
  background: #5c5c5c;
  opacity: 0.8;
}

.pdf-docs {
  display: flex;
  justify-content: start;
}

.pdf-box {
  height: 90px;
  width: 90px;
  background-color: #e5f8f1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 4px;
}

.delete-icon {
  position: absolute;
  top: 5px;
  right: 5px;
  color: #135b00;
  cursor: pointer;
}

.file-extension {
  font-size: 18px;
  color: #135b00;
  margin: 0;
  line-height: 1;
}

.file-name {
  display: block;
  font-size: 12px;
  color: #333;
  margin-top: 5px;
  text-align: center;
  word-break: break-all;
}

.docs-box {
  display: flex;
  justify-content: center;
}

.docs-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.img-item {
  display: flex;
  flex-direction: column;
  align-items: left;

}

.upload-card {
  width: 720px;
  height: 170px;
  border: 1px dashed #cccccc;
  border-radius: 8px;
  text-align: start;
}

.upload-input .v-input__control {
  text-align: center;
}

// Add 4px gap between the table header and the first row of the vintage years table
.mt-6 .v-data-table__wrapper tbody tr:first-child td {
  padding-top: 20px !important; /* Default is 16px, so 16+4=20px for a 4px extra gap */
}

.organization-list {
  max-height: 400px;
  overflow-y: auto;
}

/* Validation error styling */
.v-text-field--error .v-input__slot {
  border-color: #ff5252 !important;
}

.v-text-field--error .v-text-field__details {
  color: #ff5252;
  font-size: 12px;
  margin-top: 4px;
}

/* Data collection table alignment */
.data-collection-table .v-data-table__wrapper table tbody tr td {
  vertical-align: middle;
  padding: 12px 16px;
}

.data-collection-table .v-data-table__wrapper table thead tr th {
  vertical-align: middle;
  padding: 12px 16px;
  text-align: center;
  font-weight: 600;
  background-color: #117443;
  color: white !important;
}

/* Column-specific alignment */
.data-collection-table .v-data-table__wrapper table tbody tr td:nth-child(1) {
  text-align: left;
}

.data-collection-table .v-data-table__wrapper table tbody tr td:nth-child(2) {
  text-align: center;
}

.data-collection-table .v-data-table__wrapper table tbody tr td:nth-child(3) {
  text-align: left;
}

.data-collection-table .v-data-table__wrapper table tbody tr td:nth-child(4) {
  text-align: center;
}

.data-collection-table .v-data-table__wrapper table tbody tr td:nth-child(5) {
  text-align: center;
}

.data-collection-table .v-checkbox {
  margin: 0;
}

.data-collection-table .v-text-field {
  margin: 0;
}

.data-collection-table .v-select {
  margin: 0;
}

/* Module name field styling */
.module-name-field .v-input__slot {
  background-color: #f5f5f5 !important;
  border-color: #e0e0e0 !important;
}

.module-name-field .v-text-field__slot input {
  font-size: 14px !important;
  color: #333 !important;
  font-weight: normal !important;
}

.module-name-field .v-input__slot {
  height: 40px;
}

/* Ensure consistent height for all form elements */
.data-collection-table .v-text-field .v-input__slot {
  height: 40px !important;
}

.data-collection-table .v-select .v-input__slot {
  height: 40px !important;
}

.data-collection-table .v-checkbox {
  height: 40px;
  display: flex;
  align-items: center;
}

/* Table column width fixes */
.data-collection-table .v-data-table__wrapper table {
  table-layout: fixed;
  width: 100%;
}

.data-collection-table .v-data-table__wrapper table th:nth-child(1) {
  width: 200px;
}

.data-collection-table .v-data-table__wrapper table th:nth-child(2) {
  width: 100px;
}

.data-collection-table .v-data-table__wrapper table th:nth-child(3) {
  width: 250px;
}

.data-collection-table .v-data-table__wrapper table th:nth-child(4) {
  width: 100px;
}

.data-collection-table .v-data-table__wrapper table th:nth-child(5) {
  width: 120px;
}

/* Audio thumbnail styles */
.audio-thumbnail {
  width: 100%;
  height: 100%;
  background: #e8f5e8;
  border: 1px solid #ddd;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.audio-waveform {
  width: 80%;
  height: 60%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.waveform-bars {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  height: 100%;
}

.waveform-bar {
  background: #2e7d32;
  width: 3px;
  border-radius: 1px;
  transition: height 0.3s ease;
}

.audio-info {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.audio-icon {
  color: #2e7d32;
  font-size: 16px;
}

.audio-duration {
    color: #2e7d32;
    font-size: 12px;
    font-weight: 500;
}

.disabled {
    opacity: 0.6;
    pointer-events: none;
}

.kml-section-intro {
    color: #5f6368;
    font-size: 14px;
    line-height: 1.5;
}

.kml-field-group label {
    color: rgba(0, 0, 0, 0.87);
    font-size: 14px;
}

.kml-upload-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
}

.kml-file-input {
    flex: 1;
    min-width: 280px;
    max-width: 100%;
}

.kml-uploaded-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #00bd73;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
}

.kml-remove-btn {
    flex-shrink: 0;
    height: 40px !important;
    margin-top: 0;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
    font-size: 13px;
    border-width: 1px;
}

.kml-area-row {
    display: flex;
    align-items: stretch;
    max-width: 420px;
    gap: 0;
}

.kml-area-input {
    flex: 1;
    min-width: 0;
}

.kml-area-input ::v-deep .v-input__control,
.kml-area-input ::v-deep fieldset {
    border-radius: 4px 0 0 4px !important;
}

.kml-area-unit {
    flex: 0 0 130px;
    max-width: 130px;
}

.kml-area-unit ::v-deep .v-input__control,
.kml-area-unit ::v-deep fieldset {
    border-radius: 0 4px 4px 0 !important;
    background-color: #00bd73 !important;
    border-color: #00bd73 !important;
}

.kml-area-unit ::v-deep .v-select__selection,
.kml-area-unit ::v-deep .v-select__selection--comma {
    color: #fff !important;
    font-weight: 500;
}

.kml-area-unit ::v-deep .v-input__append-inner .v-icon {
    color: #fff !important;
}

@media (max-width: 600px) {
    .kml-upload-row {
        flex-direction: column;
        align-items: stretch;
    }

    .kml-file-input {
        min-width: 0;
    }

    .kml-remove-btn {
        width: 100%;
    }

    .kml-area-row {
        flex-direction: column;
        max-width: 100%;
    }

    .kml-area-input ::v-deep fieldset {
        border-radius: 4px 4px 0 0 !important;
    }

    .kml-area-unit {
        flex: 1 1 auto;
        max-width: 100%;
    }

    .kml-area-unit ::v-deep fieldset {
        border-radius: 0 0 4px 4px !important;
    }
}

.kml-map-wrapper {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    background-color: #fafafa;
    margin-top: 8px;
}

.kml-saved-preview {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 12px;
    background-color: #fafafa;
    max-width: 720px;
}

.kml-saved-image {
    display: block;
    width: 100%;
    max-height: 420px;
    object-fit: contain;
    border-radius: 4px;
    background-color: #ffffff;
}

</style>

<style>
.custom-file-input .v-input__slot {
  height: 40px;
  width: max-content;
  border: 1px dashed gray;
}

.custom-file-input .v-file-input__text.v-file-input__text--placeholder {
  color: green;
  font-size: 16px;
}

.custom-file-input {
  width: max-content;
}

.kml-file-input.custom-file-input {
  flex: 1;
  min-width: 280px;
  max-width: 100%;
}

.kml-file-input.custom-file-input .v-input__slot {
  width: 100% !important;
  max-width: 100%;
  min-height: 40px;
  border: 1px solid #e0e0e0;
  border-style: solid;
  background: #fff;
}

.custom-file-input .v-text-field__suffix {
  color: rgb(161, 159, 159);
  margin-left: 10px;
  font-size: 12px;
/* There is no actual issue in the selected code. The selection simply closes a CSS rule and the <style> block. */
}
</style>
